import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { SecurityManagementService } from '../../../features/security-management/security-management.service';
import { DropdownOption } from 'src/app/core/models/global.model';
import { IUserPreferencesResponse, TAddEditUserScreenType } from '../../../features/security-management/state/security-management.model';
import { UserRoleDemographicComponent } from 'src/app/features/security-management/components/user-role-demographic/user-role-demographic.component';
import { LaunchDarklyService } from 'src/app/core/layout/launch-darkly/launch-darkly.service';
import { ELaunchDarklyFlag } from 'src/app/core/constants/pol.enums';

@Component({
  selector: 'app-add-edit-user',
  templateUrl: './add-edit-user.component.html',
  styleUrls: ['./add-edit-user.component.scss']
})
export class AddEditUserComponent implements OnInit, OnDestroy {
  userForm: UntypedFormGroup = null;
  isPhysician = false;
  isExistingUser: boolean;
  isloading = false;
  isSubmitLoading = false;
  submitted = false;
  @Input() isSubmitFailed: boolean;
  @Input() failContent;
  @Input() userInfoFromParent: IUserPreferencesResponse;
  @Input() securityGroupId: string;
  /// TODO: Remove once the hard coded navigation on cancel is fixed
  @Input() preventCancelNavigation: boolean;
  stateArr = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY'
  ];
  stateOptions: DropdownOption[] = [];
  firstName: string;
  lastName: string;
  email: string;
  isEditOn = false; // EditUserFlow
  isUserProfileForm = false;
  isAddUserEditOn = false;
  @Output() sendUserForm = new EventEmitter();
  @Output() sendCancelEvent = new EventEmitter();
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private previousScreenType: TAddEditUserScreenType;
  protected addnewUserIdFlag: boolean = false;
  protected b2csigninFlag: boolean = false;

  constructor(
    private cdref: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private securityManageService: SecurityManagementService,
    private router: Router,
    private store: Store,
    private route: ActivatedRoute,
    private launchDarkly: LaunchDarklyService
  ) {
    this.launchDarkly.flags$.subscribe(res => {
      this.addnewUserIdFlag = res[ELaunchDarklyFlag.SignUp];
      this.b2csigninFlag = res[ELaunchDarklyFlag.SignIn];
    });

    console.log('HERE', this.userForm);
  }

  ngOnInit(): void {
    this.userForm = this.createForm();
    this.userForm.get('npi').disable({ onlySelf: true });
    this.isEditOn = this.router.url.includes('/edit-user');
    this.isUserProfileForm = this.router.url.includes('user/profile');
    this.isAddUserEditOn = this.router.url.includes('/add-user') && !this.router.url.includes('/add-new-user');
    if (this.userInfoFromParent) {
      this.setUserForm(this.userInfoFromParent);
      this.isPhysician = !!this.userInfoFromParent.isPhysician;
      this.updateNPIEnableStatus();
    } else {
      this.firstName = this.route.snapshot.queryParams['fname'];
      this.lastName = this.route.snapshot.queryParams['lname'];
      this.email = this.route.snapshot.queryParams['email'];

      if (this.firstName) {
        this.userForm.get('firstName').setValue(this.firstName);
        this.userForm.controls['firstName'].markAsTouched();
      }

      if (this.lastName) {
        this.userForm.get('lastName').setValue(this.lastName);
        this.userForm.controls['lastName'].markAsTouched();
      }

      if (this.email) {
        this.userForm.get('email').setValue(this.email);
        this.userForm.controls['email'].markAsTouched();
      }
    }

    this.previousScreenType = this.route.snapshot.queryParams['previousScreenType'];
    this.stateOptions = this.stateArr.map((state) => ({ label: state, value: state }));
    this.userForm.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((formvalue) => {
      // isphysician popUp NPI input:
      this.isPhysician = formvalue.physician === 'Y' ? true : false;
      this.updateNPIEnableStatus();
      // trigger change detection - getting ExpressionChangedAfterItHasBeenChecked Error - this fixes issue
      this.cdref.detectChanges();
      // check userID:
      if (formvalue.userId && this.userForm.get('userId').valid) {
        this.checkUserId(formvalue.userId);
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  createForm(): UntypedFormGroup {
    return this.fb.group({
      firstName: [this.firstName, [Validators.required]],
      mi: [''],
      lastName: [this.lastName, [Validators.required]],
      physician: ['', [Validators.required]],
      npi: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
      phoneExt: [''],
      address1: ['', [Validators.required]],
      address2: [''],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      zipcode: ['', [Validators.required]],
      userId: ['', !this.addnewUserIdFlag ? [Validators.required] : []]
    });
  }

  checkUserId(value: string): void {
    if (value && !this.isEditOn && !this.isAddUserEditOn && !this.isUserProfileForm) {
      this.isloading = true;
      this.securityManageService
        .checkExistingUserId(value)
        .pipe(
          takeUntil(this.ngUnsubscribe),
          tap(() => (this.isloading = false))
        )
        .subscribe((res) => {
          this.isExistingUser = !res?.isAvailable;
        });
    }
  }

  submitAddUser(): void {
    this.submitted = true;
    if (this.userForm.valid && !this.isExistingUser) {
      const payload = {
        ...this.userForm.value,
        securityGroupId: this.securityGroupId,

        // TODO: Can we match these form controls with user model?
        zip: this.userForm.value.zipcode,
        phoneNumber: this.userForm.value.phone,
        phoneNumberExtension: this.userForm.value.phoneExt,
        isPhysician: this.userForm.value.physician === 'Y',
        npiNumber: this.userForm.value.npi,
        middleInitial: this.userForm.value.mi
      };
      this.sendUserForm.emit(payload);
      this.isSubmitLoading = true;
    } else {
      return;
    }
  }

  // TODO: move this hard coded navigation from child to parent
  cancel() {
    this.sendCancelEvent.emit(true);
    if (!this.preventCancelNavigation) {
      const screenType = this.previousScreenType || 'summary';
      const queryParams = {
        screenType: this.isEditOn ? screenType : this.isAddUserEditOn ? 'assign-roles' : null,
        securityGroupId: sessionStorage.getItem(UserRoleDemographicComponent.DemographicsSecurityGroupIdKey),
        userName: sessionStorage.getItem(UserRoleDemographicComponent.DemographicsUserNameKey)
      };

      const AddUserUrl = this.isAddUserEditOn && this.router.url.includes('add-user/security-set-selection') ? '/security-management/add-user/security-set-selection/add-user-search/assign-user-role' : '/security-management/add-user/add-user-search/assign-user-role';

      const editUserUrl = this.isEditOn && this.router.url.includes('edit-user/multi-sets') ? '/security-management/edit-user/multi-sets/edit-existing-user' : '/security-management/edit-user/edit-existing-user';

      const routePath = this.isEditOn
        ? editUserUrl
        : this.isAddUserEditOn
          ? AddUserUrl
          : '';
      this.router.navigate([routePath], {
        queryParams: queryParams
      });
      this.userForm.reset();
    }
  }

  setUserForm(userInfo: IUserPreferencesResponse) {
    this.userForm.get('firstName').setValue(userInfo.firstName);
    this.userForm.get('lastName').setValue(userInfo.lastName);
    this.userForm.get('mi').setValue(userInfo.middleInitial);
    this.userForm.get('physician').setValue(userInfo.isPhysician ? 'Y' : 'N');
    this.userForm.get('email').setValue(userInfo.email);
    this.userForm
      .get('phone')
      .setValue(
        userInfo?.phoneNumber ? userInfo.phoneNumber?.replace(/\D+/g, '').replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3') : ''
      );
    this.userForm.get('phoneExt').setValue(userInfo.phoneNumberExtension);
    this.userForm.get('address1').setValue(userInfo.address1);
    this.userForm.get('address2').setValue(userInfo.address2);
    this.userForm.get('city').setValue(userInfo.city);
    this.userForm.get('state').setValue(userInfo.state);
    this.userForm.get('zipcode').setValue(userInfo.zip);
    this.userForm.get('userId').setValue(userInfo.preferredUserName);

    // if (!this.addnewUserIdFlag) {
    //   this.userForm.get('userId').setValue(userInfo.preferredUserName);
    // }

    if (this.userForm.get('physician').value === 'Y') {
      this.isPhysician = true;
      this.userForm.get('npi').setValue(userInfo.npiNumber);
    }

    console.log('HERE', this.userForm);
  }

  // enable npi - which checks for validation, or disable npi - which does not check validation
  updateNPIEnableStatus(): void {
    if (this.isPhysician) {
      this.userForm.get('npi').enable({ onlySelf: true });
    } else {
      this.userForm.get('npi').disable({ onlySelf: true });
    }
  }
}
